import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Typography,
    Box,
    TextField,
    Button,
    CircularProgress,
    Alert,
    AlertTitle,
    Card,
    CardContent,
    Chip,
    Grid,
    RadioGroup,
    FormControlLabel,
    Radio,
    FormControl,
    alpha,
    useTheme,
    Divider,
    Paper
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    Check as CheckIcon,
    Close as CloseIcon,
    Send as SendIcon,
    NavigateBefore as PrevIcon,
    NavigateNext as NextIcon,
    Schedule as ClockIcon,
    TrendingUp as DifficultyIcon
} from '@mui/icons-material';
import StudentLayout from 'components/layout/StudentLayout';
import RichTextDisplay from 'components/common/RichTextDisplay';
import {
    getProblemById,
    getProblemsBySession,
    submitAnswer,
    getUserProblemSubmission,
    Problem,
    ProblemSubmission
} from 'services/problemService';
import {
    getSessionById,
    getProblemGroupById,
    Session,
    ProblemGroup
} from 'services/sessionService';
import type { StudentAchievement } from 'types/achievement';
import AchievementDialog from 'components/achievements/AchievementDialog';

function getImageS3Url(imagePath: string): string {
    if (imagePath.startsWith('http')) {
        return imagePath;
    }

    const filename = imagePath.split('/').pop();
    if (!filename) {
        console.error('Invalid image path:', imagePath);
        return '';
    }

    const bucketName = process.env.REACT_APP_S3_BUCKET_NAME || 'mathwiz-images';
    const region = process.env.REACT_APP_AWS_REGION || 'us-east-1';

    return `https://${bucketName}.s3.${region}.amazonaws.com/uploads/${filename}`;
}

const ProblemDetailPageV2 = () => {
    const theme = useTheme();
    const { sessionId, problemId } = useParams<{ sessionId: string; problemId: string }>();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [problem, setProblem] = useState<Problem | null>(null);
    const [submission, setSubmission] = useState<ProblemSubmission | null>(null);
    const [answer, setAnswer] = useState('');
    const [selectedOptionId, setSelectedOptionId] = useState<number | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [hasNextProblem, setHasNextProblem] = useState(false);
    const [hasPrevProblem, setHasPrevProblem] = useState(false);
    const [nextProblemId, setNextProblemId] = useState<number | null>(null);
    const [prevProblemId, setPrevProblemId] = useState<number | null>(null);
    const [session, setSession] = useState<Session | null>(null);
    const [problemGroup, setProblemGroup] = useState<ProblemGroup | null>(null);
    const [newAchievements, setNewAchievements] = useState<StudentAchievement[]>([]);
    const [showAchievementDialog, setShowAchievementDialog] = useState(false);
    const [problemIndex, setProblemIndex] = useState<number>(0);

    const isGroupEnded = (): boolean => {
        if (!problemGroup || !problemGroup.endDate) {
            return false;
        }

        const now = new Date();
        const endDate = new Date(problemGroup.endDate);
        const nowDateOnly = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const endDateOnly = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());

        return nowDateOnly > endDateOnly;
    };

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                setError(null);
                setSubmission(null);
                setAnswer('');
                setSelectedOptionId(null);

                if (!problemId || !sessionId) {
                    setError('Invalid problem or session ID');
                    setLoading(false);
                    return;
                }

                const problemIdNum = parseInt(problemId);
                const sessionIdNum = parseInt(sessionId);

                const fetchedProblem = await getProblemById(problemIdNum);

                if (!fetchedProblem) {
                    setError('Problem not found');
                    setLoading(false);
                    return;
                }

                setProblem(fetchedProblem);

                const [
                    fetchedSession,
                    fetchedProblemGroup,
                    allSessionProblems,
                    existingSubmission
                ] = await Promise.all([
                    getSessionById(sessionIdNum),
                    getProblemGroupById(fetchedProblem.groupId),
                    getProblemsBySession(sessionIdNum),
                    getUserProblemSubmission(problemIdNum)
                ]);

                setSession(fetchedSession);
                setProblemGroup(fetchedProblemGroup);

                const problemsInSameGroup = allSessionProblems.filter(p => p.groupId === fetchedProblem.groupId);
                const sortedProblems = [...problemsInSameGroup].sort((a, b) => a.id - b.id);
                const currentIndex = sortedProblems.findIndex(p => Number(p.id) === Number(problemIdNum));

                let actualIndex = currentIndex;

                if (actualIndex === -1 && sortedProblems.length > 0) {
                    const closestIndex = sortedProblems.findIndex(p => Number(p.id) >= Number(problemIdNum));
                    actualIndex = closestIndex !== -1 ? closestIndex : 0;
                }

                const hasPrev = actualIndex > 0;
                const hasNext = actualIndex < sortedProblems.length - 1;

                setProblemIndex(actualIndex);
                setHasPrevProblem(hasPrev);
                setHasNextProblem(hasNext);

                if (hasPrev) {
                    setPrevProblemId(sortedProblems[actualIndex - 1].id);
                }

                if (hasNext) {
                    setNextProblemId(sortedProblems[actualIndex + 1].id);
                }

                if (existingSubmission) {
                    const submissionData = Array.isArray(existingSubmission)
                        ? existingSubmission[0]
                        : existingSubmission;

                    if (submissionData) {
                        setSubmission(submissionData);

                        if (fetchedProblem.problemType !== 'multiple_choice' && submissionData.submittedAnswer) {
                            setAnswer(submissionData.submittedAnswer);
                        }
                    }
                }
            } catch (err) {
                console.error('Error fetching problem:', err);
                setError('Failed to load problem. Please try again later.');
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [problemId, sessionId]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        try {
            setSubmitting(true);
            setError(null);
            setNewAchievements([]);

            if (!problemId) {
                setError('Problem ID is missing');
                return;
            }

            let result;

            if (problem?.problemType === 'multiple_choice') {
                if (selectedOptionId === null) {
                    setError('Please select an option');
                    setSubmitting(false);
                    return;
                }

                result = await submitAnswer(parseInt(problemId), '', selectedOptionId);
            } else {
                if (!answer || !answer.trim()) {
                    setError('Please enter an answer');
                    setSubmitting(false);
                    return;
                }

                result = await submitAnswer(parseInt(problemId), answer.trim());
            }

            setSubmission(result.submission);

            if (result.achievementEarned && result.newAchievements && result.newAchievements.length > 0) {
                setNewAchievements(result.newAchievements);
                setShowAchievementDialog(true);
            }

            setError(null);
        } catch (err) {
            const error = err as Error;
            console.error('Error submitting answer:', error);
            setError(error.message || 'Failed to submit answer. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    const goBack = () => {
        navigate(`/v2/sessions/${sessionId}/problems`);
    };

    const getDifficultyLabel = (level: number): string => {
        const labels = ['Very Easy', 'Easy', 'Medium', 'Hard', 'Very Hard'];
        return labels[level - 1] || 'Unknown';
    };

    const getDifficultyColor = (level: number) => {
        if (level <= 2) return theme.palette.success.main;
        if (level <= 3) return theme.palette.primary.main;
        if (level <= 4) return theme.palette.warning.main;
        return theme.palette.error.main;
    };

    if (loading) {
        return (
            <StudentLayout>
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
                    <CircularProgress />
                </Box>
            </StudentLayout>
        );
    }

    return (
        <StudentLayout>
            <Box sx={{ maxWidth: 900, mx: 'auto' }}>
                {/* Breadcrumb */}
                <Box sx={{ mb: 3 }}>
                    <Button
                        startIcon={<ArrowBackIcon />}
                        onClick={goBack}
                        variant="outlined"
                        sx={{ mb: 2 }}
                    >
                        Back to Problems
                    </Button>
                    {session && problemGroup && (
                        <Typography variant="body2" color="text.secondary">
                            {session.title} • {problemGroup.title} • Problem {problemIndex + 1}
                        </Typography>
                    )}
                </Box>

                {error && !problem ? (
                    <Alert severity="error" sx={{ mb: 3 }}>
                        {error}
                    </Alert>
                ) : problem ? (
                    <>
                        {/* Problem Card */}
                        <Card
                            elevation={0}
                            sx={{
                                borderRadius: 3,
                                border: `1px solid ${theme.palette.divider}`,
                                mb: 3,
                                overflow: 'hidden'
                            }}
                        >
                            {/* Header */}
                            <Box
                                sx={{
                                    p: 3,
                                    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.secondary.main, 0.08)} 100%)`,
                                    borderBottom: `1px solid ${theme.palette.divider}`
                                }}
                            >
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                    <Typography variant="h5" fontWeight="bold">
                                        Problem {problemIndex + 1}
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                        <Chip
                                            icon={<DifficultyIcon />}
                                            label={getDifficultyLabel(problem.difficultyLevel)}
                                            size="small"
                                            sx={{
                                                bgcolor: alpha(getDifficultyColor(problem.difficultyLevel), 0.15),
                                                color: getDifficultyColor(problem.difficultyLevel),
                                                fontWeight: 600
                                            }}
                                        />
                                        {submission?.isCorrect && (
                                            <Chip
                                                icon={<CheckIcon />}
                                                label="Solved"
                                                size="small"
                                                color="success"
                                            />
                                        )}
                                    </Box>
                                </Box>

                                {problemGroup && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                        <Chip
                                            label={problemGroup.title}
                                            size="small"
                                            variant="outlined"
                                        />
                                        {submission && (
                                            <Typography variant="caption" color="text.secondary">
                                                Attempts: {submission.attemptCount}
                                            </Typography>
                                        )}
                                    </Box>
                                )}
                            </Box>

                            {/* Problem Content */}
                            <CardContent sx={{ p: 4 }}>
                                <RichTextDisplay
                                    content={problem.problemStatement}
                                    variant="body1"
                                    sx={{
                                        fontSize: '1.2rem',
                                        lineHeight: 2,
                                        fontWeight: 400,
                                        color: 'text.primary',
                                        mb: 4,
                                        '& p': {
                                            marginBottom: 2.5
                                        },
                                        '& strong': {
                                            fontWeight: 700,
                                            color: theme.palette.primary.dark
                                        }
                                    }}
                                />

                                {problem.imagePath && (
                                    <Paper
                                        elevation={0}
                                        sx={{
                                            my: 3,
                                            p: 3,
                                            textAlign: 'center',
                                            bgcolor: alpha(theme.palette.grey[100], 0.5),
                                            border: `1px solid ${theme.palette.divider}`,
                                            borderRadius: 2
                                        }}
                                    >
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2, fontWeight: 600 }}>
                                            Problem Diagram
                                        </Typography>
                                        <Box sx={{ display: 'inline-block' }}>
                                            <img
                                                src={getImageS3Url(problem.imagePath)}
                                                alt="Problem diagram"
                                                style={{
                                                    maxWidth: '100%',
                                                    maxHeight: '400px',
                                                    display: 'block',
                                                    borderRadius: '8px'
                                                }}
                                                onError={(e) => {
                                                    console.error('Image failed to load:', problem.imagePath);
                                                    const target = e.target as HTMLImageElement;
                                                    target.onerror = null;
                                                    target.style.display = 'none';
                                                }}
                                            />
                                        </Box>
                                    </Paper>
                                )}

                                <Divider sx={{ my: 4 }} />

                                {/* Submission Result */}
                                {submission && (
                                    <Alert
                                        severity={submission.isCorrect ? 'success' : 'error'}
                                        icon={submission.isCorrect ? <CheckIcon /> : <CloseIcon />}
                                        sx={{
                                            mb: 3,
                                            borderRadius: 2,
                                            '& .MuiAlert-icon': {
                                                fontSize: 28
                                            }
                                        }}
                                    >
                                        <AlertTitle sx={{ fontWeight: 700 }}>
                                            {submission.isCorrect ? 'Correct Answer!' : 'Incorrect Answer'}
                                        </AlertTitle>
                                        {submission.isCorrect ? (
                                            <>
                                                Great job! You've successfully solved this problem.
                                                {submission.attemptCount > 1 && (
                                                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                                                        It took you {submission.attemptCount} {submission.attemptCount === 1 ? 'attempt' : 'attempts'}.
                                                    </Typography>
                                                )}
                                            </>
                                        ) : (
                                            <>
                                                Your answer was not correct. You can modify your answer and try again.
                                                <Typography variant="body2" sx={{ mt: 0.5 }}>
                                                    Attempt #{submission.attemptCount}
                                                </Typography>
                                            </>
                                        )}
                                    </Alert>
                                )}

                                {/* Submission Form */}
                                {isGroupEnded() ? (
                                    <Alert
                                        severity="info"
                                        icon={<ClockIcon />}
                                        sx={{ borderRadius: 2 }}
                                    >
                                        <AlertTitle sx={{ fontWeight: 700 }}>Submission Period Ended</AlertTitle>
                                        The submission period for this problem group has ended. You can still view the problem and your previous submissions, but cannot submit new answers.
                                    </Alert>
                                ) : (!submission || !submission.isCorrect) && (
                                    <Box
                                        component="form"
                                        onSubmit={handleSubmit}
                                        sx={{
                                            p: 3,
                                            bgcolor: alpha(theme.palette.primary.main, 0.03),
                                            borderRadius: 2,
                                            border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`
                                        }}
                                    >
                                        <Typography variant="h6" gutterBottom fontWeight="bold" sx={{ mb: 2 }}>
                                            Your Answer
                                        </Typography>

                                        {error && (
                                            <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
                                                {error}
                                            </Alert>
                                        )}

                                        {problem.problemType === 'multiple_choice' ? (
                                            <FormControl component="fieldset" fullWidth>
                                                <RadioGroup
                                                    value={selectedOptionId || ''}
                                                    onChange={(e) => setSelectedOptionId(parseInt(e.target.value, 10))}
                                                >
                                                    {problem.options?.map(option => (
                                                        <Paper
                                                            key={option.id}
                                                            elevation={0}
                                                            sx={{
                                                                mb: 1.5,
                                                                border: `2px solid ${selectedOptionId === option.id ? theme.palette.primary.main : theme.palette.divider}`,
                                                                borderRadius: 2,
                                                                transition: 'all 0.2s',
                                                                '&:hover': {
                                                                    borderColor: theme.palette.primary.main,
                                                                    bgcolor: alpha(theme.palette.primary.main, 0.03)
                                                                }
                                                            }}
                                                        >
                                                            <FormControlLabel
                                                                value={option.id}
                                                                control={<Radio />}
                                                                label={option.optionText}
                                                                disabled={submitting}
                                                                sx={{
                                                                    m: 0,
                                                                    p: 2,
                                                                    width: '100%',
                                                                    '& .MuiFormControlLabel-label': {
                                                                        fontSize: '1rem',
                                                                        fontWeight: selectedOptionId === option.id ? 600 : 400
                                                                    }
                                                                }}
                                                            />
                                                        </Paper>
                                                    ))}
                                                </RadioGroup>
                                            </FormControl>
                                        ) : (
                                            <TextField
                                                fullWidth
                                                value={answer}
                                                onChange={(e) => setAnswer(e.target.value)}
                                                placeholder="Enter your answer here..."
                                                variant="outlined"
                                                disabled={submitting}
                                                multiline
                                                rows={3}
                                                sx={{
                                                    mb: 2,
                                                    '& .MuiOutlinedInput-root': {
                                                        bgcolor: 'background.paper',
                                                        fontSize: '1.1rem'
                                                    }
                                                }}
                                            />
                                        )}

                                        <Button
                                            type="submit"
                                            variant="contained"
                                            size="large"
                                            disabled={submitting || (problem.problemType === 'open_ended' ? !answer.trim() : selectedOptionId === null)}
                                            startIcon={submitting ? <CircularProgress size={20} /> : <SendIcon />}
                                            sx={{
                                                borderRadius: 2,
                                                px: 4,
                                                py: 1.5,
                                                fontSize: '1rem',
                                                fontWeight: 700
                                            }}
                                        >
                                            {submitting ? 'Submitting...' : 'Submit Answer'}
                                        </Button>
                                    </Box>
                                )}
                            </CardContent>
                        </Card>

                        {/* Navigation */}
                        <Grid container spacing={2}>
                            {hasPrevProblem && prevProblemId && (
                                <Grid item xs={hasNextProblem ? 6 : 12}>
                                    <Button
                                        fullWidth
                                        variant="outlined"
                                        size="large"
                                        startIcon={<PrevIcon />}
                                        onClick={() => navigate(`/v2/sessions/${sessionId}/problems/${prevProblemId}`)}
                                        sx={{ borderRadius: 2, py: 1.5 }}
                                    >
                                        Previous Problem
                                    </Button>
                                </Grid>
                            )}

                            {hasNextProblem && nextProblemId ? (
                                <Grid item xs={hasPrevProblem ? 6 : 12}>
                                    <Button
                                        fullWidth
                                        variant="contained"
                                        size="large"
                                        endIcon={<NextIcon />}
                                        onClick={() => navigate(`/v2/sessions/${sessionId}/problems/${nextProblemId}`)}
                                        sx={{ borderRadius: 2, py: 1.5 }}
                                    >
                                        Next Problem
                                    </Button>
                                </Grid>
                            ) : !hasPrevProblem && (
                                <Grid item xs={12}>
                                    <Button
                                        fullWidth
                                        variant="outlined"
                                        size="large"
                                        startIcon={<ArrowBackIcon />}
                                        onClick={() => navigate(`/v2/sessions/${sessionId}/problems`)}
                                        sx={{ borderRadius: 2, py: 1.5 }}
                                    >
                                        Back to Problems
                                    </Button>
                                </Grid>
                            )}
                        </Grid>
                    </>
                ) : (
                    <Alert severity="warning">Problem not found</Alert>
                )}
            </Box>

            <AchievementDialog
                open={showAchievementDialog}
                onClose={() => setShowAchievementDialog(false)}
                newAchievements={newAchievements}
                onViewTrophyCase={() => navigate('/student/achievements')}
            />
        </StudentLayout>
    );
};

export default ProblemDetailPageV2;
